import { Geo } from "src/gen/schema/models/global";

/**
 * Submits a request as a native `<form>` POST, causing a full page navigation.
 *
 * When Fred 307-redirects an org-creation request to a different regional cell,
 * `fetch()` fails because the browser enforces CORS on the cross-origin
 * redirect response.  A native form submission is top-level navigation—CORS
 * does not apply—so the browser follows the entire redirect chain
 * (307 → SSO 302 → callback → landing page) naturally.
 */
function submitFormPost(action: string, fields: Record<string, string>): void {
  const url = new URL(action, window.location.origin);
  if (window.cnc?.xCsrfToken) {
    url.searchParams.set("xCsrfToken", window.cnc.xCsrfToken);
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = url.toString();
  form.style.display = "none";

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * Cross-geo add-org via form POST (Fred.addOrg replacement).
 * Targets POST /api/v1/organizations — Fred will 307 to the correct cell's
 * /ui/v1/geoUserOrganizations, which initiates SSO and redirects to /add-org
 * on the target cell where the org is created same-geo.
 */
export function submitAddOrgViaFormPost(orgName: string, targetGeo: Geo): void {
  submitFormPost("/api/v1/organizations", {
    orgName,
    targetGeo: String(targetGeo)
  });
}

/**
 * Cross-geo new-org signup via form POST (Fred.newOrg replacement).
 * Targets POST /ui/v1/organizations — Fred will 307 to the correct cell's
 * /ui/v1/geoUserRegistration, which initiates SSO and redirects to /signup
 * on the target cell where the org is created same-geo.
 */
export function submitNewOrgViaFormPost(
  orgName: string,
  email: string,
  eulaVersion: string,
  targetGeo: Geo,
  timezone: string,
  theme: number
): void {
  submitFormPost("/ui/v1/organizations", {
    "signUp.companyName": orgName,
    "signUp.email": email,
    "signUp.eulaVersion": eulaVersion,
    "userPreferences.timezone": timezone,
    "userPreferences.theme": String(theme),
    targetGeo: String(targetGeo)
  });
}
